import { useDispatch, useSelector } from 'react-redux';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@/model';
import { SessionType } from '@shared';
import { createShortTermCareFundingSource } from '@/features/shop-insurance';
import { withShopInsuranceQuoteAddedToPlanEvent } from '@/analytics/posthog-api-success';

interface UseShortTermCarePolicyManagementProps {
  quote: ShortTermCareQuote;
  fundingSourceId?: string | null;
}

/**
 * Add/remove a short-term care quote as an ltcPolicy funding source. Mirrors
 * useTraditionalLTCPolicyManagement — same dispatch, same funding source type;
 * only the funding-source builder differs.
 */
export function useShortTermCarePolicyManagement({
  quote,
  fundingSourceId = null,
}: UseShortTermCarePolicyManagementProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const handleAddPolicy = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!client) return;

    dispatch(
      withShopInsuranceQuoteAddedToPlanEvent(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: createShortTermCareFundingSource(
            quote,
            advisor?.advisorId,
            isAdvisorSession,
          ),
          fundingSourceId: null,
        }),
        {
          clientId: client.clientId,
          quoteId: quote.shortTermCareQuoteId,
          quoteType: 'short-term-care',
        },
      ),
    );
  };

  const handleRemovePolicy = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!client || !fundingSourceId) return;

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  return { handleAddPolicy, handleRemovePolicy };
}
